/**
 * Checks if a string is in base64 format, returning boolean
 *
 * @param {string} string String to test
 */
export const isBase64 = (string) => {
  const regex =
    /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/
  return regex.test(string)
}

import { LitElement, html } from '@hh/global.js'

class AdaptiveImage extends LitElement {
  constructor() {
    super()
    // This list of asset widths to select from will be comprehensive for most use cases
    this.srcsetWidths = [493, 600, 713, 823, 1100, 1206, 1346, 1426, 1646, 1946]

    const imageData = isBase64(this.dataset.image)
      ? atob(this.dataset.image)
      : this.dataset.image
    try {
      this.image = JSON.parse(imageData)
    } catch (e) {
      this.image = imageData
    }

    const mobileImageData = isBase64(this.dataset.mobileImage)
      ? atob(this.dataset.mobileImage)
      : this.dataset.mobileImage
    try {
      this.mobileImage = JSON.parse(mobileImageData)
    } catch (e) {
      this.mobileImage = mobileImageData
    }

    this.width = this.image.width || 1946
    this.mobileWidth = this?.mobileImage?.width || 750
    this.src = this.image?.src || this.image
    this.mobileSrc = this.mobileImage?.src || this.mobileImage
    this.alt = (this.dataset.alt && this.dataset.alt.trim() !== "")
      ? this.dataset.alt : (this.image?.alt && this.image.alt.trim() !== "")
        ? this.image.alt : '';
    this.sizes = this.dataset.sizes || '100vw'
    this.loading = this.dataset.loading || 'lazy'
    this.objectFit = this.dataset.objectFit || 'cover'
    this.aspectRatio =
      parseFloat(this.dataset.aspectRatio) || this.image?.aspect_ratio || 1
    this.minAspectRatio = parseFloat(this.dataset.minAspectRatio) || 0
    this.transform = this.dataset.transform
    this.mobileAspectRatio = parseFloat(this.dataset.mobileAspectRatio) || this.mobileImage?.aspect_ratio || 1

    this.style.setProperty('--hh-object-fit', this.objectFit)
    if (this.aspectRatio >= this.minAspectRatio) {
      this.style.setProperty('--hh-aspect-ratio', this.aspectRatio)
    } else {
      this.style.setProperty('--hh-aspect-ratio', this.minAspectRatio)
    }
    this.style.setProperty('--hh-mobile-aspect-ratio', this.mobileAspectRatio)
    this.style.setProperty('--hh-transform', this.transform)

    this.attachShadow({ mode: 'open' })
  }

  static get observedAttributes() {
    return ['data-image']
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-image') {
      try {
        this.updateImage(JSON.parse(atob(newValue)))
      } catch (error) {
        this.updateImage(newValue)
      }
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.render()
  }

  updateImage(data) {
    Object.assign(this, data)

    this.requestUpdate()
  }

  generateSrcset(src, imgWidth) {
    let srcset = ''

    for (const width of this.srcsetWidths) {
      if (imgWidth >= width) srcset += `${src}&width=${width} ${width}w,`
    }
    srcset += `${src} ${imgWidth}w`
    return srcset
  }

  show() {
    this.style.display = 'block'
  }

  hide() {
    this.style.display = 'none'
  }

  render() {
    return html`
      <style>
        :host {
          display: block;
          height: auto;
          position: relative;
          width: 100%;
          aspect-ratio: ${this.mobileAspectRatio
        ? 'var(--hh-mobile-aspect-ratio)'
        : 'var(--hh-aspect-ratio)'};
        }

        #image-element {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          object-fit: var(--hh-object-fit);
          transform: var(--hh-transform);
        }
        @media screen and (min-width: 768px) {
          :host {
            aspect-ratio: var(--hh-aspect-ratio);
          }
        }
        @supports not (aspect-ratio: 'var(--hh-aspect-ratio)') {
          :host::before {
            float: left;
            padding-top: calc(100% / 'var(--hh-aspect-ratio)');
            content: '';
          }
          :host::after {
            display: block;
            clear: both;
            content: '';
          }
        }
      </style>
      <picture>
        ${this.mobileImage
        ? html`<source
                media="(min-width: 768px)"
                srcset="${this.generateSrcset(this.src, this.width)}"
                sizes="${this.sizes}"
              />
              <source
                media="(max-width: 767px)"
                srcset="
                  ${this.generateSrcset(this.mobileSrc, this.mobileWidth)}
                "
                sizes="${this.sizes}"
              />`
        : html`<source srcset="${this.generateSrcset(
          this.src,
          this.width
        )}" sizes="${this.sizes}"></source>`}

        <img
          id="image-element"
          src="${this.src}&width=${this.srcsetWidths[0]}"
          alt="${this.alt}"
          loading="${this.loading}"
        />
      </picture>
    `
  }
}

customElements.define('adaptive-image', AdaptiveImage)
